import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { USER_PROFILE, USER_ROLE } from '../../services/auth';
import { resetPassword } from '../../services/branch_users';
import { toast } from 'react-toastify';

const BranchUserForm = ({ user, branches, dataErrors, onSave, onCancel, restrictToBranchUsersOnly }) => {

  const isEdit = Boolean(user);
  const role = localStorage.getItem(USER_ROLE);
  const profile = JSON.parse(localStorage.getItem(USER_PROFILE));
  const showBranches = (role == 'admin');
  const selectedBranch = branches.find(branch => branch?.id === user?.branch?.id);

  const [formData, setFormData] = useState({
    id: user?.id || 0,
    name: user?.name,
    phone: user?.phone,
    email: user?.email,
    selected_branch: selectedBranch,
    branch: showBranches ? selectedBranch : profile.branch,
    branch_id: showBranches ? selectedBranch?.id : profile.branch?.id,
    password: user?.password,
    role: user?.role || (!restrictToBranchUsersOnly ? 'branch_admin' : 'branch_user'),
    is_active: user?.is_active || 1
  });
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePassword, setChangePassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changePasswordError, setChangePasswordError] = useState("");

  const updateBranch = (index) => {
    setFormData({ ...formData, branch_id: branches[index].id, branch: branches[index] });
  }

  const handleInputChange = (field) => (e) => {
    let value = e.target.value;
    if (field === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    } else if (field === 'name' || field === 'email') {
      value = value.toUpperCase();
    }
    setFormData({ ...formData, [field]: value });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.phone) newErrors.phone = 'Phone is required';
    if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Invalid phone number';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) newErrors.email = 'Invalid email';
    if (formData.id == 0) {
      if (!formData.password) newErrors.password = 'Password is required';
    }
    if (showBranches) {
      if (!formData.branch || formData.branch <= 0) newErrors.branch = 'Select atleast one branch.';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }
    onSave(formData);
  };

  const handleChangePassword = () => {
    setChangePassword("");
    setConfirmPassword("");
    setChangePasswordError("");
    setShowResetPassword(false);
    setShowChangePasswordModal(true);
  };

  const changePasswordInServer = async (onSuccess, onFailed) => {
    let params = { password: changePassword };
    await resetPassword(formData.id, params).then(([err, data]) => {
      onSuccess(err, data);
    })
    .catch((error) => {
      onFailed(error);
    });
  };

  const handlePasswordResetSubmit = () => {
    if (!changePassword || !confirmPassword) {
      setChangePasswordError("Both fields are required");
      return;
    }

    // if (changePassword.length < 8) {
    //   setChangePasswordError("Password must be at least 8 characters");
    //   return;
    // }

    if (changePassword !== confirmPassword) {
      setChangePasswordError("Passwords do not match");
      return;
    }

    // update only password
    changePasswordInServer((err, data) => {
      if (!err) {
        setShowChangePasswordModal(false);
        toast.success("Password Saved Successfully.");
      }
    }, (error) => {
    });

  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 [&_input[type='text']]:uppercase [&_input:disabled]:uppercase [&_select]:uppercase">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={handleInputChange('name')}
            className="w-full mt-1 px-3 py-2 border rounded"
          />
          {validationErrors.name && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
          )}
          {dataErrors.name && (
            <p className="text-red-500 text-sm mt-1">{dataErrors.name[0]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Phone</label>
          <input
              type="tel"
              required
              pattern="[0-9]{10}"
              title="Enter a valid 10-digit phone number"
              value={formData.phone}
              onChange={handleInputChange('phone')}
              className="w-full mt-1 px-3 py-2 border rounded"
          />
          {validationErrors.phone && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
          )}
          {dataErrors.phone && (
            <p className="text-red-500 text-sm mt-1">{dataErrors.phone[0]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
              type="text"
              required
              title="Enter a valid email"
              value={formData.email}
              onChange={handleInputChange('email')}
              className="w-full mt-1 px-3 py-2 border rounded"
          />
          {validationErrors.email && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
          )}
          {dataErrors.email && (
            <p className="text-red-500 text-sm mt-1">{dataErrors.email[0]}</p>
          )}
        </div>

        { !isEdit && <div>
          <label className="block text-sm font-medium text-gray-700">Password</label>
          <div className="relative mt-1">
            <input
              type={showFormPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder={isEdit ? '••••••' : ''}
              required={!isEdit}
              className="w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring focus:border-blue-300 normal-case [text-transform:none]"
              style={{ textTransform: 'none' }}
              />
            <button
              type="button"
              onClick={() => setShowFormPassword(!showFormPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showFormPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-500" />
              ) : (
                  <EyeIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {validationErrors.password && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>
            )}
            {dataErrors.password && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.password[0]}</p>
            )}
          </div>
        </div>}

        {
          showBranches && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Branch</label>
              <select
                required
                value={formData.selected_branch?.name}
                onChange={(e) => updateBranch(e.target.selectedIndex-1)}
                disabled={isEdit}
                className="w-full mt-1 px-3 py-2 border rounded bg-white disabled:opacity-75"
              >
                <option value="">Select Branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
              {validationErrors.branch && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.branch}</p>
              )}
              {dataErrors.branch && (
                <p className="text-red-500 text-sm mt-1">{dataErrors.branch[0]}</p>
              )}
            </div>
          )
        }
      </div>

      <div className="flex justify-end space-x-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-300 rounded">
          Cancel
        </button>
        {isEdit && (
          <button
            type="button"
            onClick={handleChangePassword}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            Change Password
          </button>
        )}
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Save
        </button>
      </div>
      {showChangePasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">Reset Password</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">New Password</label>
                <input
                  type={showResetPassword ? "text" : "password"}
                  name="new_password"
                  value={changePassword}
                  onChange={(e) => setChangePassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 normal-case [text-transform:none]"
                  style={{ textTransform: 'none' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Confirm Password</label>
                <input
                  type={showResetPassword ? "text" : "password"}
                  name="confirm_password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 normal-case [text-transform:none]"
                  style={{ textTransform: 'none' }}
                />
              </div>

              <button
                type="button"
                onClick={() => setShowResetPassword(!showResetPassword)}
                className="text-sm text-blue-600 hover:underline"
              >
                {showResetPassword ? "Hide Password" : "Show Password"}
              </button>

              {changePasswordError && (
                <p className="text-red-500 text-sm">{changePasswordError}</p>
              )}
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => setShowChangePasswordModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handlePasswordResetSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default BranchUserForm;
